import MyLocationIcon from "@mui/icons-material/MyLocation";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";
import {
  DEVICE_LOCATION_STATE,
  HOME_ADDRESS_FALLBACK_COPY,
  HOME_ADDRESS_FALLBACK_WITH_HISTORY_COPY,
  LAST_KNOWN_POSITION_LABEL,
  STALE_FIX_SUFFIX,
  buildDeviceLocationTiles,
  buildLastKnownHeader,
  buildLastKnownPosition,
  buildLastKnownTiles,
  buildNoReportHeadline,
  deviceLocationState,
  formatDeviceLocationAt,
  isStaleDeviceFix,
} from "./deviceLocationFormatters";

/**
 * Light caregiver Device Location cards on Client Profile (NOT Command Center).
 * Expects shared weather state from useClientWeather (same GET as Weather & Hazards).
 *
 * GMA-1945: a device that reported while stationary and a device that never
 * reported at all must not render alike. A stationary fix keeps its tiles and
 * reads "Not moving"; an absent report shows no tile grid at all, says so, and
 * labels the home-address coordinates it is falling back to.
 */

const LABEL_COLOR = "#4a4a4a";
const SECONDARY_COLOR = "#616161";

/** The metric tile grid, shared by the live-fix and last-known renders. */
function TileGrid({ tiles, tileBorderColor }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: "10px",
      }}
    >
      {tiles.map((tile) => (
        <Tooltip key={tile.key} title={tile.hint} arrow>
          <div
            data-testid={`device-location-tile-${tile.key}`}
            tabIndex={0}
            role="group"
            aria-label={`${tile.label}: ${tile.ariaValue}. ${tile.hint}`}
            style={{
              background: "#fff",
              border: `1px solid ${tileBorderColor}`,
              borderRadius: 8,
              padding: "12px 10px",
              minHeight: 72,
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: LABEL_COLOR,
                marginBottom: 4,
              }}
            >
              {tile.label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{tile.value}</div>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

export default function DeviceLocationSection({
  loading = false,
  error = null,
  data = null,
  onRefresh,
}) {
  const location = data?.location;
  const state = deviceLocationState(location);
  // Only the reported branch below renders these, so a second state guard here
  // would be an unreachable construct rather than a safeguard.
  const tiles = buildDeviceLocationTiles(location);
  const updatedLabel = formatDeviceLocationAt(location?.deviceLocationAt);
  const stale = isStaleDeviceFix(location);
  // #725: where the client was when the device last reported, and when. Null
  // on fix rows that predate the position fields, so nothing else changes.
  const lastPosition = buildLastKnownPosition(location?.lastDeviceFix);
  // A device that stopped reporting is a fault a caregiver must notice, so the
  // card drops its healthy-green accent in that one state — and only that one:
  // "no GPS fix yet" is an unconfigured device, not a device that went quiet.
  const noReport =
    !loading && !error && Boolean(data) && state === DEVICE_LOCATION_STATE.NO_REPORT;

  return (
    <div
      className="device-location-section"
      data-testid="device-location-section"
      data-state={!loading && !error && data ? state : undefined}
      style={{
        backgroundColor: noReport ? "#FFF8E1" : "#E8F5E9",
        color: "#333",
        border: `1px solid ${noReport ? "#FFE082" : "#C8E6C9"}`,
        borderLeft: `4px solid ${noReport ? "#F57C00" : "#43A047"}`,
        borderRadius: 8,
        padding: 16,
        marginTop: 16,
        marginBottom: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MyLocationIcon fontSize="small" />
          <h3 style={{ margin: 0 }}>Device location</h3>
        </div>
        <button
          type="button"
          aria-label="Refresh device location"
          onClick={onRefresh}
          disabled={loading || !onRefresh}
          style={{
            border: "none",
            background: "none",
            cursor: loading ? "default" : "pointer",
            opacity: loading ? 0.5 : 1,
          }}
        >
          <RefreshIcon fontSize="small" />
        </button>
      </div>

      {/* Mounted on EVERY render, empty until there is something to say.
          role="status" announces changes to a live region that already exists;
          inserting a region that is already populated is not reliably spoken,
          so the visible amber block below carries no role of its own and this
          region is what actually announces the fault after a refresh. */}
      <div
        data-testid="device-location-live"
        role="status"
        aria-live="polite"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {noReport ? buildNoReportHeadline(location) : ""}
      </div>

      {loading && <p style={{ color: SECONDARY_COLOR }}>Loading location…</p>}
      {!loading && error && (
        <p style={{ color: "#b71c1c" }}>
          Device location is temporarily unavailable. Please try again.
        </p>
      )}

      {!loading && !error && data && (
        <>
          {state === DEVICE_LOCATION_STATE.NO_COORDS ? (
            <p style={{ color: SECONDARY_COLOR, fontStyle: "italic" }}>
              No GPS fix yet. When the client&apos;s MyCompass device reports
              location, elevation, speed, and direction appear here.
            </p>
          ) : state === DEVICE_LOCATION_STATE.NO_REPORT ? (
            /* Nothing recent arrived from the device. This has to read as a
               fault first, with the coordinates on screen labelled as the home
               address they actually are — and then, when the payload carries
               the last known fix (#644), show those readings dated below it
               rather than throwing away data the device did send. */
            <div data-testid="device-location-no-report" style={{ marginTop: 8 }}>
              <p
                style={{
                  margin: 0,
                  marginBottom: 6,
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  // 5.3:1 on the #FFF8E1 amber ground. #E65100 measured 3.6:1,
                  // under the 4.5:1 this text needs at 1.05rem bold.
                  color: "#BF360C",
                }}
              >
                {buildNoReportHeadline(location)}
              </p>
              <p style={{ margin: 0, color: "#5D4037", lineHeight: 1.5 }}>
                {location?.lastDeviceFix
                  ? HOME_ADDRESS_FALLBACK_WITH_HISTORY_COPY
                  : HOME_ADDRESS_FALLBACK_COPY}
              </p>
              {location?.lastDeviceFix && (
                <>
                  {lastPosition && (
                    /* Coordinates as text on purpose: a client's position is
                       care data, and a deep link to a third-party map service
                       is a privacy decision, not a card feature (#725). */
                    <p
                      data-testid="device-location-last-position"
                      style={{
                        color: "#3E2723",
                        marginTop: 14,
                        marginBottom: 0,
                        fontWeight: 600,
                      }}
                    >
                      {`${LAST_KNOWN_POSITION_LABEL} · ${lastPosition.coords}${
                        lastPosition.when ? ` · ${lastPosition.when}` : ""
                      }`}
                    </p>
                  )}
                  <p
                    data-testid="device-location-last-known"
                    style={{
                      color: SECONDARY_COLOR,
                      marginTop: 14,
                      marginBottom: 12,
                      fontWeight: 600,
                    }}
                  >
                    {buildLastKnownHeader(location.lastDeviceFix)}
                  </p>
                  <TileGrid
                    tiles={buildLastKnownTiles(location.lastDeviceFix)}
                    tileBorderColor="#FFE082"
                  />
                </>
              )}
            </div>
          ) : (
            <>
              <p
                data-testid="device-location-updated"
                style={{ color: SECONDARY_COLOR, marginTop: 8, marginBottom: 12 }}
              >
                {`Last device fix${updatedLabel ? ` · ${updatedLabel}` : ""}${
                  stale ? ` · ${STALE_FIX_SUFFIX}` : ""
                }`}
              </p>
              <TileGrid tiles={tiles} tileBorderColor="#A5D6A7" />
            </>
          )}
        </>
      )}
    </div>
  );
}
